import { faro, LogLevel } from "@grafana/faro-web-sdk";

import { linkPostHogReplayToFaro } from "./posthog/linkReplayToFaro";

const RESOURCE_ELEMENT_TAGS = new Set([
  "IMG",
  "SCRIPT",
  "LINK",
  "AUDIO",
  "VIDEO",
  "SOURCE",
  "TRACK",
  "IFRAME",
  "EMBED",
  "OBJECT",
]);

const MAX_LOGS_PER_CATEGORY = 50;

const RATE_LIMIT_WINDOW_MS = 60_000;

// Vite chunk/CSS preload failures surface as unhandled rejections with these
// messages; they are reported separately as chunk.load-failed.
const CHUNK_LOAD_MESSAGE_PATTERN =
  /dynamically imported module|Unable to preload CSS|Importing a module script failed/i;

const URL_IN_MESSAGE_PATTERN = /https?:\/\/[^\s'")]+/g;

// blockedURI keyword values (not URLs) — resolving them against the origin
// would mislabel blockedHost as our own host.
const CSP_KEYWORD_URIS = new Set([
  "inline",
  "eval",
  "wasm-eval",
  "data",
  "blob",
  "filesystem",
  "about",
]);

type CategoryWindow = {
  windowStartedAt: number;
  loggedCount: number;
  suppressedCount: number;
  seenDedupeKeys: Set<string>;
};

function sanitizeUrlForLog(url: string): string {
  if (!url) return "";

  if (url.startsWith("data:")) return "data:[truncated]";

  try {
    const parsed = new URL(url, window.location.origin);
    parsed.search = "";
    parsed.username = "";
    parsed.password = "";

    return parsed.toString();
  } catch {
    return url.split("?")[0];
  }
}

function extractHost(url: string): string {
  if (!url || url.startsWith("data:")) return "";

  try {
    return new URL(url, window.location.origin).host;
  } catch {
    return "";
  }
}

function getResourceUrl(target: HTMLElement): string {
  if (target instanceof HTMLLinkElement) return target.href;

  if (target instanceof HTMLObjectElement) return target.data;

  if ("src" in target) return String((target as HTMLImageElement).src);

  return "";
}

function isCrossOriginScrubbed(event: ErrorEvent): boolean {
  if (!event.message) return true;

  if (event.message === "Script error.") return true;

  if (event.message === "Script error") return true;

  return event.error == null && !event.filename;
}

function getAppVersion(): string {
  try {
    return faro.metas.value.app?.version ?? "";
  } catch {
    return "";
  }
}

let teardownPreviousListeners: (() => void) | undefined;

export function setupCrashReporting(): void {
  teardownPreviousListeners?.();

  linkPostHogReplayToFaro();

  const categoryWindows = new Map<string, CategoryWindow>();

  function flushSuppressedSummary(
    category: string,
    categoryWindow: CategoryWindow,
  ): void {
    if (categoryWindow.suppressedCount === 0) return;

    faro.api.pushLog(
      [
        `[Telemetry] suppressed ${categoryWindow.suppressedCount} ${category} events in the previous window (deduped or over limit)`,
      ],
      {
        level: LogLevel.WARN,
        context: {
          type: "telemetry.suppressed",
          category,
          suppressedCount: String(categoryWindow.suppressedCount),
          sessionId: faro.api.getSession()?.id ?? "unknown",
          pathname: window.location.pathname,
        },
      },
    );
  }

  function getCurrentWindow(category: string): CategoryWindow {
    const now = Date.now();
    const existingWindow = categoryWindows.get(category);

    if (
      existingWindow &&
      now - existingWindow.windowStartedAt < RATE_LIMIT_WINDOW_MS
    ) {
      return existingWindow;
    }

    if (existingWindow) flushSuppressedSummary(category, existingWindow);

    const freshWindow: CategoryWindow = {
      windowStartedAt: now,
      loggedCount: 0,
      suppressedCount: 0,
      seenDedupeKeys: new Set(),
    };
    categoryWindows.set(category, freshWindow);

    return freshWindow;
  }

  function shouldLog(category: string, dedupeKey: string): boolean {
    const categoryWindow = getCurrentWindow(category);
    const isDuplicate = categoryWindow.seenDedupeKeys.has(dedupeKey);
    const isOverLimit = categoryWindow.loggedCount >= MAX_LOGS_PER_CATEGORY;

    if (isDuplicate || isOverLimit) {
      categoryWindow.suppressedCount += 1;

      return false;
    }

    categoryWindow.seenDedupeKeys.add(dedupeKey);
    categoryWindow.loggedCount += 1;

    return true;
  }

  function handleJsError(event: ErrorEvent): void {
    if (isCrossOriginScrubbed(event)) return;

    if (!shouldLog("crash.unhandled", event.message)) return;

    faro.api.pushLog([`[Crash] Unhandled error: ${event.message}`], {
      level: LogLevel.ERROR,
      context: {
        type: "crash.unhandled",
        source: "window.onerror",
        sessionId: faro.api.getSession()?.id ?? "unknown",
        filenameHost: extractHost(event.filename),
        lineno: String(event.lineno),
        colno: String(event.colno),
        stack: event.error instanceof Error ? (event.error.stack ?? "") : "",
        pathname: window.location.pathname,
      },
    });
  }

  function handleResourceError(target: HTMLElement): void {
    if (!RESOURCE_ELEMENT_TAGS.has(target.tagName)) return;
    const resourceUrl = getResourceUrl(target);
    const sanitizedUrl = sanitizeUrlForLog(resourceUrl);

    if (!sanitizedUrl) return;

    if (!shouldLog("asset.failed", `${target.tagName}|${sanitizedUrl}`)) {
      return;
    }

    faro.api.pushLog(
      [`[Asset] failed to load ${target.tagName}: ${sanitizedUrl}`],
      {
        level: LogLevel.ERROR,
        context: {
          type: "asset.failed",
          tagName: target.tagName,
          host: extractHost(resourceUrl),
          ownOrigin: String(extractHost(resourceUrl) === window.location.host),
          online: String(navigator.onLine),
          sessionId: faro.api.getSession()?.id ?? "unknown",
          pathname: window.location.pathname,
        },
      },
    );
  }

  function handleErrorEvent(event: Event): void {
    if (event instanceof ErrorEvent) {
      handleJsError(event);

      return;
    }

    if (event.target instanceof HTMLElement) handleResourceError(event.target);
  }

  function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const reason: unknown = event.reason;
    const message = getReasonMessage(reason);

    // Reported as chunk.load-failed via vite:preloadError — skip to avoid
    // double-counting the same failure.
    if (CHUNK_LOAD_MESSAGE_PATTERN.test(message)) return;

    if (!shouldLog("crash.unhandled", message)) return;

    faro.api.pushLog([`[Crash] Unhandled promise rejection: ${message}`], {
      level: LogLevel.ERROR,
      context: {
        type: "crash.unhandled",
        source: "unhandledrejection",
        sessionId: faro.api.getSession()?.id ?? "unknown",
        stack: reason instanceof Error ? (reason.stack ?? "") : "",
        pathname: window.location.pathname,
      },
    });
  }

  function handleChunkLoadError(event: Event): void {
    const payload: unknown = (event as Event & { payload?: unknown }).payload;
    const message =
      payload instanceof Error ? payload.message : String(payload ?? "unknown");
    const sanitizedMessage = message.replace(
      URL_IN_MESSAGE_PATTERN,
      sanitizeUrlForLog,
    );

    if (!shouldLog("chunk.load-failed", sanitizedMessage)) return;

    const chunkUrl = message.match(URL_IN_MESSAGE_PATTERN)?.[0] ?? "";

    faro.api.pushLog([`[Chunk] failed to load: ${sanitizedMessage}`], {
      level: LogLevel.ERROR,
      context: {
        type: "chunk.load-failed",
        chunkHost: extractHost(chunkUrl),
        appVersion: getAppVersion(),
        online: String(navigator.onLine),
        stack: payload instanceof Error ? (payload.stack ?? "") : "",
        sessionId: faro.api.getSession()?.id ?? "unknown",
        pathname: window.location.pathname,
      },
    });
  }

  function handleCspViolation(event: SecurityPolicyViolationEvent): void {
    const isKeywordBlockedUri = CSP_KEYWORD_URIS.has(event.blockedURI);
    const blockedUrl = isKeywordBlockedUri
      ? ""
      : sanitizeUrlForLog(event.blockedURI);
    const dedupeKey = `${event.violatedDirective}|${blockedUrl || event.blockedURI}`;

    if (!shouldLog("csp.violation", dedupeKey)) return;

    const level =
      event.disposition === "report" ? LogLevel.WARN : LogLevel.ERROR;

    faro.api.pushLog(
      [
        `[CSP] ${event.violatedDirective} blocked ${
          blockedUrl || `(${event.blockedURI || "inline"})`
        }`,
      ],
      {
        level,
        context: {
          type: "csp.violation",
          violatedDirective: event.violatedDirective,
          effectiveDirective: event.effectiveDirective,
          blockedHost: isKeywordBlockedUri ? "" : extractHost(event.blockedURI),
          disposition: event.disposition,
          sourceHost: extractHost(event.sourceFile),
          lineNumber: String(event.lineNumber),
          columnNumber: String(event.columnNumber),
          // Only clue for inline violations (blockedURI is "inline"); the
          // policy caps it at 40 chars of the violating snippet.
          sample: event.sample || "",
          documentHost: extractHost(event.documentURI),
          sessionId: faro.api.getSession()?.id ?? "unknown",
          pathname: window.location.pathname,
        },
      },
    );
  }

  window.addEventListener("error", handleErrorEvent, true);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.addEventListener("securitypolicyviolation", handleCspViolation);
  window.addEventListener("vite:preloadError", handleChunkLoadError);

  teardownPreviousListeners = () => {
    window.removeEventListener("error", handleErrorEvent, true);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("securitypolicyviolation", handleCspViolation);
    window.removeEventListener("vite:preloadError", handleChunkLoadError);
  };
}

function getReasonMessage(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message;
  }

  if (typeof reason === "string") {
    return reason;
  }

  return "Unknown rejection";
}
